import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { catchError, map, Observable, of } from 'rxjs';

import {
  DeleteSwapFeeTypeResult,
  SwapFeeType,
  SwapFeeTypeApiItem,
  UpsertSwapFeeTypeRequest,
} from './swap-fee-type.types';
import { apiUrl } from '../../../auth/api-url';

@Injectable({
  providedIn: 'root',
})
export class SwapFeeTypeService {
  private readonly endpoint = apiUrl('swapfeetype');

  constructor(private readonly http: HttpClient) {}

  getSwapFeeTypes(): Observable<SwapFeeType[]> {
    return this.http.get<unknown>(this.endpoint).pipe(
      map((response) => this.mapRows(response, (item) => this.normalizeSwapFeeType(item))),
    );
  }

  createSwapFeeType(payload: UpsertSwapFeeTypeRequest): Observable<void> {
    return this.http.post<void>(this.endpoint, payload);
  }

  updateSwapFeeType(id: number, payload: UpsertSwapFeeTypeRequest): Observable<void> {
    return this.http.put<void>(`${this.endpoint}/${id}`, payload);
  }

  deleteSwapFeeType(id: number): Observable<DeleteSwapFeeTypeResult> {
    return this.http.delete<void>(`${this.endpoint}/${id}`).pipe(
      map((): DeleteSwapFeeTypeResult => ({ kind: 'success' })),
      catchError(() => of<DeleteSwapFeeTypeResult>({
        kind: 'error',
        message: 'Unable to delete swap fee type at this time. Please try again.',
      })),
    );
  }

  private normalizeSwapFeeType(item: SwapFeeTypeApiItem): SwapFeeType {
    return {
      id: this.toNumber(item.id),
      name: this.toText(item.name),
      bps: this.toNullableNumber(item.bps),
    };
  }

  private mapRows<T>(response: unknown, mapper: (item: SwapFeeTypeApiItem) => T): T[] {
    return this.extractRows(response).map(mapper);
  }

  private extractRows(response: unknown): SwapFeeTypeApiItem[] {
    if (Array.isArray(response)) {
      return response as SwapFeeTypeApiItem[];
    }

    if (response && typeof response === 'object') {
      const record = response as Record<string, unknown>;
      const data = record['data'];
      const items = record['items'];

      if (Array.isArray(data)) {
        return data as SwapFeeTypeApiItem[];
      }

      if (Array.isArray(items)) {
        return items as SwapFeeTypeApiItem[];
      }
    }

    return [];
  }

  private toText(value: unknown): string {
    return typeof value === 'string' ? value : '';
  }

  private toNumber(value: unknown): number {
    return typeof value === 'number' && Number.isFinite(value) ? value : 0;
  }

  private toNullableNumber(value: unknown): number | null {
    return typeof value === 'number' && Number.isFinite(value) ? value : null;
  }
}
